<script lang="ts">
import type { NestedSelectMenuItem, NestedSelectMenuItemBase, NestedSelectMenuTheme } from './types';

export interface NestedSelectMenuItemContentProps<
	TItem extends NestedSelectMenuItemBase = NestedSelectMenuItem,
> {
	item: TItem;
	index?: number;
	ui: NestedSelectMenuTheme;
	isRtl?: boolean;
	showChevron?: boolean;
}
</script>

<script setup lang="ts" generic="TItem extends NestedSelectMenuItemBase = NestedSelectMenuItem">
import { computed } from 'vue';
import { useAppConfig } from '#app/config';

import { hasChildren } from './utils';

const {
	index = 0,
	isRtl = false,
	showChevron = true,
} = defineProps<NestedSelectMenuItemContentProps<TItem>>();

defineSlots<{
	'item'?: (props: { item: TItem; index: number }) => any;
	'item-leading'?: (props: { item: TItem; index: number }) => any;
	'item-label'?: (props: { item: TItem; index: number }) => any;
	'item-trailing'?: (props: { item: TItem; index: number }) => any;
}>();

const appConfig = useAppConfig();

const chevronIcon = computed(() =>
	isRtl ? appConfig.ui.icons.chevronLeft : appConfig.ui.icons.chevronRight,
);
</script>

<template>
	<slot name="item" :item :index>
		<slot name="item-leading" :item :index>
			<DIcon
				v-if="item.icon"
				:name="item.icon"
				data-slot="itemLeadingIcon"
				:class="ui.itemLeadingIcon({ class: item.ui?.itemLeadingIcon })"
			/>
		</slot>

		<span
			data-slot="itemWrapper"
			:class="ui.itemWrapper({ class: item.ui?.itemWrapper })"
		>
			<span
				data-slot="itemLabel"
				:class="ui.itemLabel({ class: item.ui?.itemLabel })"
			>
				<slot name="item-label" :item :index>
					{{ item.label }}
				</slot>
			</span>
		</span>

		<span
			data-slot="itemTrailing"
			:class="ui.itemTrailing({ class: item.ui?.itemTrailing })"
		>
			<slot name="item-trailing" :item :index />

			<DIcon
				v-if="showChevron && hasChildren(item)"
				:name="chevronIcon"
				data-slot="itemTrailingIcon"
				:class="ui.itemTrailingIcon({ class: item.ui?.itemTrailingIcon })"
			/>
		</span>
	</slot>
</template>
